import { useCallback, useEffect, useState } from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import { api, ApiError, type AuditLogEntry } from "../api.js";
import { useToken } from "../auth.js";

// Admin audit log §"Journal d'audit admin". Filter by entity, entity id, or
// admin, and click any row to expand the JSON diff. Retention is 7 years on
// the backend; the UI paginates through pages of 100 at a time.

export function AuditLog() {
  const token = useToken();
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [entity, setEntity] = useState<string>("");
  const [entityId, setEntityId] = useState<string>("");
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const limit = 100;

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.auditLog(token, {
        entity: entity || undefined,
        entityId: entityId || undefined,
        limit,
        offset,
      });
      setEntries(res.entries);
      setTotal(res.total);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [token, entity, entityId, offset]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const { t } = useLingui();

  return (
    <div>
      <header className="page-header">
        <h1><Trans>Admin audit log</Trans></h1>
        <div className="row-actions">
          <label>
            <Trans>Entity</Trans>{" "}
            <input
              value={entity}
              onChange={(e) => {
                setEntity(e.target.value);
                setOffset(0);
              }}
              placeholder={t`plan / customer / license…`}
            />
          </label>
          <label>
            <Trans>Entity ID</Trans>{" "}
            <input
              value={entityId}
              onChange={(e) => {
                setEntityId(e.target.value);
                setOffset(0);
              }}
              placeholder={t`uuid`}
            />
          </label>
          <button className="ghost" onClick={() => void refresh()}>
            <Trans>Refresh</Trans>
          </button>
        </div>
      </header>
      {error && <div className="banner err">{error}</div>}
      {loading ? (
        <p className="muted"><Trans>Loading…</Trans></p>
      ) : (
        <>
          <p className="muted">
            <Trans>
              {total} total entries — showing {offset + 1}–{Math.min(offset + entries.length, total)}
            </Trans>
          </p>
          <table className="table">
            <thead>
              <tr>
                <th><Trans>When (UTC)</Trans></th>
                <th><Trans>Admin</Trans></th>
                <th><Trans>Entity</Trans></th>
                <th><Trans>Action</Trans></th>
                <th><Trans>ID</Trans></th>
                <th />
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <>
                  <tr key={e.id}>
                    <td>{new Date(e.createdAt).toISOString().replace("T", " ").slice(0, 19)}</td>
                    <td>{e.adminEmail ?? <span className="muted">—</span>}</td>
                    <td>
                      <code>{e.entity}</code>
                    </td>
                    <td>{e.action}</td>
                    <td>
                      {e.entityId ? (
                        <code>{e.entityId.slice(0, 8)}…</code>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td>
                      <button className="link-btn" onClick={() => toggle(e.id)}>
                        {expanded.has(e.id) ? <Trans>Hide diff</Trans> : <Trans>Diff</Trans>}
                      </button>
                    </td>
                  </tr>
                  {expanded.has(e.id) && (
                    <tr key={`${e.id}-diff`}>
                      <td colSpan={6}>
                        <pre className="code-block">
                          {JSON.stringify(e.diff, null, 2)}
                        </pre>
                      </td>
                    </tr>
                  )}
                </>
              ))}
              {entries.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted" style={{ textAlign: "center" }}>
                    <Trans>No audit entries match.</Trans>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="row-actions" style={{ marginTop: "1rem" }}>
            <button
              className="ghost"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - limit))}
            >
              <Trans>← Newer</Trans>
            </button>
            <button
              className="ghost"
              disabled={offset + entries.length >= total}
              onClick={() => setOffset(offset + limit)}
            >
              <Trans>Older →</Trans>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
